import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/js/bootstrap.bundle.js';
import { createApp } from "vue";
import axios from 'axios';

import App from './App.vue';
import router from './router';
import store from './store';

import { createI18n } from 'vue-i18n';
import ja from './views/localeLanguage/ja';
import tw from './views/localeLanguage/tw';

const SUPPORTED_LOCALES = ['ja', 'tw'];
const localeFromStorage = localStorage.getItem('locale');
const initialLocale = SUPPORTED_LOCALES.includes(localeFromStorage)
  ? localeFromStorage
  : 'ja';

const i18n = createI18n({
  legacy: false,
  locale: initialLocale,
  messages: {
    ja,
    tw,
  },
})

// Keep persisted locale deterministic across full-page navigations.
localStorage.setItem('locale', initialLocale);
document.documentElement.lang = initialLocale;

const app = createApp(App);

axios.defaults.withCredentials = true;

axios.defaults.baseURL = process.env.VUE_APP_BACKEND_URL || 'http://localhost:5000/';   // the FastAPI backend

//axios.defaults.baseURL = 'http://localhost:5001/';   // the FastAPI backend
console.log( "baseURL:", axios.defaults.baseURL)

axios.interceptors.response.use(undefined, async function (error) {
  console.log('🔍 [AXIOS INTERCEPTOR] Error intercepted:', {
    status: error.response?.status,
    url: error.config?.url,
    method: error.config?.method
  });
  
  if (error) {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔍 [AXIOS INTERCEPTOR] 401 error detected');
      originalRequest._retry = true;
      
      // 認証不要なページでは自動ログアウトを実行しない
      const currentRoute = router.currentRoute.value.fullPath;
      const matchedRoutes = router.currentRoute.value.matched;
      
      console.log('🔍 [AXIOS INTERCEPTOR] Current route info:', {
        currentRoute,
        matched: matchedRoutes.map(record => ({
          path: record.path,
          name: record.name,
          meta: record.meta
        }))
      });
      
      // ルーターガードと同じロジックを使用
      let isAuthRequired = false;
      
      for (let i = matchedRoutes.length - 1; i >= 0; i--) {
        const record = matchedRoutes[i];
        
        // 明示的に認証不要が設定されている場合はfalse
        if (record.meta.requiresAuth === false) {
          isAuthRequired = false;
          break;
        }
        // 明示的に認証必要が設定されている場合はtrue
        if (record.meta.requiresAuth === true) {
          isAuthRequired = true;
          break;
        }
        // デフォルトは親ルートの設定に従う
        if (record.meta.requireAuth === true) {
          isAuthRequired = true;
        }
      }
      
      console.log('🔍 [AXIOS INTERCEPTOR] Auth requirement check:', {
        isAuthRequired,
        userState: store.getters.stateUser,
        isAuthenticated: store.getters.isAuthenticated
      });
      
      // 認証が必要なページでのみログアウト処理を実行
      if (isAuthRequired) {
        console.log('⚠️ [AXIOS INTERCEPTOR] Executing logout and redirect');
        await store.dispatch('logOut');

        // forwriting 有自己的 login
        if( currentRoute.startsWith( "/forwriting/signin")) {
          router.push('/forwriting/signin');
        }
        else if( currentRoute.startsWith( "/forwriting")) {
          router.push({ path: '/forwriting/login', query: { reason: 'session_expired' } });
        } else {
          router.push({ path: '/login', query: { reason: 'session_expired' } });
        }

        // Session timeout is handled globally by redirect and friendly login hint.
        // Return a resolved response-like object to avoid surfacing raw 401 errors in UI.
        return {
          data: null,
          status: 401,
          statusText: 'Unauthorized',
          headers: {},
          config: originalRequest,
        };
      } else {
        console.log('ℹ️ [AXIOS INTERCEPTOR] Auth not required, skipping logout');
      }
    }

    return Promise.reject(error);
  }
});

app.use(router);
app.use(store);
app.use(i18n);

// ルーターガードの設定
router.beforeEach((to, from, next) => {
  console.log('🔍 [ROUTER GUARD] Navigation:', {
    from: from.path,
    to: to.path,
    matched: to.matched.map(record => ({
      path: record.path,
      name: record.name,
      meta: record.meta
    }))
  });

  // 子ルートから親ルートまで、明示的に認証不要が設定されているかチェック
  let requiresAuth = false;
  let authDecision = 'default';
  
  for (let i = to.matched.length - 1; i >= 0; i--) {
    const record = to.matched[i];
    
    console.log(`🔍 [ROUTER GUARD] Checking route[${i}]:`, {
      path: record.path,
      name: record.name,
      meta: record.meta
    });
    
    // 明示的に認証不要が設定されている場合はfalse
    if (record.meta.requiresAuth === false) {
      requiresAuth = false;
      authDecision = `requiresAuth=false at ${record.name || record.path}`;
      break;
    }
    // 明示的に認証必要が設定されている場合はtrue
    if (record.meta.requiresAuth === true) {
      requiresAuth = true;
      authDecision = `requiresAuth=true at ${record.name || record.path}`;
      break;
    }
    // デフォルトは親ルートの設定に従う
    if (record.meta.requireAuth === true) {
      requiresAuth = true;
      authDecision = `requireAuth=true at ${record.name || record.path}`;
    }
  }

  console.log('🔍 [ROUTER GUARD] Auth decision:', {
    requiresAuth,
    authDecision,
    isAuthenticated: store.getters.isAuthenticated,
    userState: store.getters.stateUser
  });

  // 認証不要なページの場合は、認証チェックをスキップ
  if (!requiresAuth) {
    console.log('✅ [ROUTER GUARD] Auth not required, proceeding');
    next();
    return;
  }

  // 認証が必要な場合、ログイン状態をチェック
  if (store.getters.isAuthenticated) {
    console.log('✅ [ROUTER GUARD] User authenticated, proceeding');
    next(); // 認証済みの場合は次へ
  } else {
    console.log('❌ [ROUTER GUARD] User not authenticated, redirecting');
    // 未認証の場合は適切なログインページにリダイレクト
    if (to.path.startsWith('/forwriting')) {
      // 無限リダイレクトを防ぐため、現在のパスが既にログインページでない場合のみリダイレクト
      if (to.path !== '/forwriting/login' && to.path !== '/forwriting/signin') {
        console.log('🔄 [ROUTER GUARD] Redirecting to /forwriting/login');
        next('/forwriting/login');
      } else {
        console.log('✅ [ROUTER GUARD] Already on login page, proceeding');
        next(); // 既にログインページの場合はそのまま進む
      }
    } else {
      console.log('🔄 [ROUTER GUARD] Redirecting to /login');
      next({
        path: '/login',
        query: { redirect: to.fullPath },
      });
    }
  }
});

// npm install --save @ckeditor/ckeditor5-vue @ckeditor/ckeditor5-build-classic
import { CkeditorPlugin } from '@ckeditor/ckeditor5-vue';
app.use(CkeditorPlugin);

app.mount("#app");
